/**
 * Bounded browser cache for timed calendar ranges using the historical storage key. Keeps the 12
 * most recently written ranges and filters all-day and imported Canvas/Book-Tracker events unless
 * they are protected holds. Entries are keyed by range, not by user.
 *
 * @module apps/web/src/lib/calendarCache
 */

import type { CalendarEvent } from '@/stores/scheduleStore';

/** Historical localStorage namespace for calendar ranges; renaming it would abandon existing cached entries. */
const CALENDAR_CACHE_KEY = 'flexiplanner-calendar-events-v2';
/** Upper bound on retained ranges to limit browser storage growth. */
const MAX_CACHE_ENTRIES = 12;

/** One requested calendar interval, its write timestamp, and the renderable timed commitments cached for that interval. */
interface CachedCalendarRange {
  /** Verbatim start/end range identity; it does not include the account ID. */
  key: string;
  /** ISO write timestamp used to retain the most recent twelve ranges. */
  savedAt: string;
  /** Renderable timed commitments cached for the requested interval. */
  events: CalendarEvent[];
}

/** Join the requested range endpoints verbatim into a cache identity. No timezone normalization or user ID is added. */
function rangeKey(start: string, end: string): string {
  return `${start}..${end}`;
}

/** Read the historical localStorage range map. Unavailable storage, invalid JSON, or non-object data falls back to an empty map; individual entry shapes are not deeply validated. */
function readCache(): Record<string, CachedCalendarRange> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(CALENDAR_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/** Retain the twelve most recently written ranges, then serialize the map to localStorage. Browser absence is a no-op; storage write errors propagate to the caller. */
function writeCache(cache: Record<string, CachedCalendarRange>) {
  if (typeof window === 'undefined') return;
  const entries = Object.values(cache)
    .sort((a, b) => Date.parse(b.savedAt) - Date.parse(a.savedAt))
    .slice(0, MAX_CACHE_ENTRIES);
  window.localStorage.setItem(
    CALENDAR_CACHE_KEY,
    JSON.stringify(Object.fromEntries(entries.map(entry => [entry.key, entry])))
  );
}

/**
 * Read a range entry from localStorage, tolerating unavailable or malformed stored data and filtering non-renderable commitments. Returns null when no cached range exists.
 */
export function getCachedCalendarEvents(start: string, end: string): CalendarEvent[] | null {
  const entry = readCache()[rangeKey(start, end)];
  return entry?.events?.filter(isRenderableCalendarEvent) || null;
}

/**
 * Store renderable commitments for a range, retaining at most 12 entries. Writes to browser localStorage and may propagate storage quota or access errors.
 */
export function setCachedCalendarEvents(start: string, end: string, events: CalendarEvent[]) {
  const cache = readCache();
  const key = rangeKey(start, end);
  cache[key] = {
    key,
    savedAt: new Date().toISOString(),
    events: events.filter(isRenderableCalendarEvent),
  };
  writeCache(cache);
}

/** Exclude date-only/all-day entries and imported Canvas or Book-Tracker activities from timed commitment rendering. Protected Fluxability holds remain visible after the timed-event check, preventing duplicate imported work on the calendar. */
function isRenderableCalendarEvent(event: CalendarEvent): boolean {
  if (isAllDayCalendarEvent(event)) return false;
  if (event.isFlexHold) return true;

  const attributes = event.customAttributes || {};
  const source = String(attributes.source || '').toLowerCase();
  return source !== 'canvas' && source !== 'book-tracker' && !attributes.canvas && !attributes.book_tracker;
}

/** Treat either endpoint without a time separator as all-day/untimed. This is a display-shape check, not a full timestamp validator. */
function isAllDayCalendarEvent(event: { /** Optional event start text; absence of a time separator marks the event untimed. */ start?: string; /** Optional event end text; absence of a time separator marks the event untimed. */ end?: string }): boolean {
  return !String(event.start || '').includes('T') || !String(event.end || '').includes('T');
}
